import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Database, Trash2, Check, AlertCircle, Loader2, StopCircle, Pause } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker, getLocaleFromLang } from '../../utils/localizedContent';


const TCG_INFO = {
  pokemon:  { name: 'Pokemon TCG',          color: '#D4A84B', estimate: '~18,000 cards' },
  magic:    { name: 'Magic: The Gathering',  color: '#0D5E4A', estimate: '~100,000 cards' },
  yugioh:   { name: 'Yu-Gi-Oh!',            color: '#7B3F00', estimate: '~13,000 cards' },
  onepiece: { name: 'One Piece TCG',        color: '#C45B4A', estimate: '~2,000 cards' },
  riftbound:{ name: 'Riftbound',            color: '#0BC4E2', estimate: '~500 cards' },
};

export default function AdminCardSync({ lang, setMessage }) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoverStop, setHoverStop] = useState(null); // tcgType with hover on sync btn while syncing

  const l = createTextPicker(lang);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/card-sync/status`);
      setStatuses(data.statuses || []);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleSync = async (tcgType) => {
    try {
      const { data } = await axios.post(`${API}/admin/card-sync/start/${tcgType}`);
      setMessage(data.message || l('Sincronización iniciada', 'Sync started'));
      fetchStatus();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleStop = async (tcgType) => {
    try {
      const { data } = await axios.post(`${API}/admin/card-sync/stop/${tcgType}`);
      setMessage(data.message || l('Sincronización detenida', 'Sync stopped'));
      fetchStatus();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleClear = async (tcgType) => {
    if (!window.confirm(l(`¿Eliminar todas las cartas de ${TCG_INFO[tcgType]?.name}?`, `Clear all ${TCG_INFO[tcgType]?.name} cards?`))) return;
    try {
      const { data } = await axios.post(`${API}/admin/card-sync/clear/${tcgType}`);
      setMessage(data.message);
      fetchStatus();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const hasAnySyncing = statuses.some(s => s.is_active === true);
  const getLedState = (status, cardCount) => {
    const health = status.sync_health;
    if (health === 'complete') {
      return {
        label: l('Completado', 'Completed'),
        dotClass: 'bg-[#22C55E] shadow-[0_0_10px_rgba(34,197,94,0.8)]',
      };
    }
    if (health === 'incomplete' || cardCount > 0) {
      return {
        label: l('Incompleto', 'Incomplete'),
        dotClass: 'bg-[#EAB308] shadow-[0_0_10px_rgba(234,179,8,0.75)]',
      };
    }
    return {
      label: l('Vacío', 'Empty'),
      dotClass: 'bg-transparent border border-[#3F3F46]',
    };
  };
  const getCatalogReadiness = (status) => {
    if (status.is_full_sync) {
      return {
        label: l('Catálogo local listo', 'Local catalog ready'),
        className: 'bg-[#10B981]/20 text-[#34D399]',
      };
    }
    if (status.status === 'syncing') {
      return {
        label: l('Preparando catálogo local', 'Preparing local catalog'),
        className: 'bg-[#C9A962]/20 text-[#C9A962]',
      };
    }
    return {
      label: l('Usando fallback externo', 'Using external fallback'),
      className: 'bg-[#F59E0B]/20 text-[#F59E0B]',
    };
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-white font-bold text-xl">{l('Sincronización de Cartas', 'Card Sync')}</h2>
        {hasAnySyncing && (
          <span className="flex items-center gap-2 text-[#C9A962] text-sm">
            <Loader2 size={14} className="animate-spin" />
            {l('Sincronizando...', 'Syncing...')}
          </span>
        )}
      </div>
      <p className="text-[#A1A1AA] text-sm mb-6">
        {l(
          'Descarga las cartas de cada TCG a la base de datos local para navegación instantánea sin depender de APIs externas.',
          'Download cards from each TCG to the local database for instant browsing without external API dependency.'
        )}
      </p>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 size={24} className="animate-spin text-[#C9A962] mx-auto" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(TCG_INFO).map(([tcgType, info]) => {
            const status = statuses.find(s => s.tcg_type === tcgType) || {};
            const cardCount = status.db_card_count || 0;
            const isSyncing = status.is_active === true;
            const isCompleted = !isSyncing && status.status === 'completed' && cardCount > 0;
            const isStopped = !isSyncing && status.status === 'stopped';
            const hasError = status.status === 'error';
            const isHoveringForStop = hoverStop === tcgType;
            const readiness = getCatalogReadiness(status);
            const led = getLedState(status, cardCount);

            return (
              <div key={tcgType} className="bg-[#12121A] border border-white/5 rounded-xl p-5" data-testid={`sync-card-${tcgType}`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${led.dotClass}`} title={led.label} />
                    <h3 className="text-white font-semibold text-sm">{info.name}</h3>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isCompleted && <Check size={15} className="text-[#34D399]" />}
                    {isStopped && <StopCircle size={15} className="text-[#F59E0B]" />}
                    {hasError && <AlertCircle size={15} className="text-[#EF4444]" />}
                    {isSyncing && <Loader2 size={15} className="animate-spin" style={{ color: info.color }} />}
                  </div>
                </div>

                {/* Status text */}
                <div className="mb-2 flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    isSyncing   ? 'bg-[#C9A962]/20 text-[#C9A962]' :
                    isCompleted ? 'bg-[#10B981]/20 text-[#34D399]' :
                    isStopped   ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                    hasError    ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                                  'bg-white/5 text-[#52525B]'
                  }`}>
                    {isSyncing ? l('Sincronizando', 'Syncing') :
                     isCompleted ? l('Completado', 'Completed') :
                     isStopped   ? l('Detenido', 'Stopped') :
                     hasError    ? l('Error', 'Error') :
                                   l('Inactivo', 'Idle')}
                  </span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${readiness.className}`}>
                    {readiness.label}
                  </span>
                </div>

                {/* Card count */}
                <div className="flex items-center gap-2 mb-2">
                  <Database size={12} className="text-[#A1A1AA]" />
                  <span className="text-[#A1A1AA] text-xs">
                    {cardCount > 0
                      ? `${cardCount.toLocaleString()} ${l('cartas en DB', 'cards in DB')}`
                      : l('Sin datos locales', 'No local data')}
                  </span>
                </div>

                <p className="text-[#A1A1AA]/60 text-xs mb-3">{info.estimate}</p>

                {(status.processed_cards || status.inserted_cards || status.updated_cards) && (
                  <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                    <div className="bg-[#0A0A0E] rounded-lg p-2 border border-white/5">
                      <p className="text-[#A1A1AA] mb-0.5">{l('Procesadas', 'Processed')}</p>
                      <p className="text-white font-semibold">{(status.processed_cards || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A0A0E] rounded-lg p-2 border border-white/5">
                      <p className="text-[#A1A1AA] mb-0.5">{l('Insertadas', 'Inserted')}</p>
                      <p className="text-[#34D399] font-semibold">{(status.inserted_cards || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A0A0E] rounded-lg p-2 border border-white/5">
                      <p className="text-[#A1A1AA] mb-0.5">{l('Actualizadas', 'Updated')}</p>
                      <p className="text-[#C9A962] font-semibold">{(status.updated_cards || 0).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {/* Progress bar */}
                {isSyncing && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-[#A1A1AA] mb-1">
                      <span>{(status.synced_cards || 0).toLocaleString()} / {status.total_cards ? status.total_cards.toLocaleString() : '?'}</span>
                      <span>{status.progress_pct || 0}%</span>
                    </div>
                    <div className="h-2 bg-[#0A0A0E] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500 animate-pulse"
                        style={{ width: `${status.progress_pct || 0}%`, backgroundColor: info.color }}
                      />
                    </div>
                  </div>
                )}

                {/* Last synced */}
                {status.last_synced && !isSyncing && (
                  <p className="text-[#A1A1AA]/60 text-xs mb-3">
                    {l('Sincronizado:', 'Last sync:')}{' '}
                    {new Date(status.last_synced).toLocaleDateString(getLocaleFromLang(lang), {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                )}

                {/* Error */}
                {hasError && status.error_message && (
                  <p className="text-[#EF4444] text-xs mb-3 truncate" title={status.error_message}>{status.error_message}</p>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {/* Sync / Stop button */}
                  {isSyncing ? (
                    <button
                      onClick={() => handleStop(tcgType)}
                      onMouseEnter={() => setHoverStop(tcgType)}
                      onMouseLeave={() => setHoverStop(null)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all
                        bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20 border border-[#EF4444]/20"
                      data-testid={`sync-btn-${tcgType}`}
                    >
                      {isHoveringForStop ? (
                        <><Pause size={12} />{l('Detener', 'Stop')}</>
                      ) : (
                        <><Loader2 size={12} className="animate-spin" />{l('Sincronizando...', 'Syncing...')}</>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSync(tcgType)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-colors
                        bg-[#C9A962]/20 text-[#C9A962] hover:bg-[#C9A962]/30"
                      data-testid={`sync-btn-${tcgType}`}
                    >
                      <RefreshCw size={12} />
                      {l('Iniciar sync', 'Start sync')}
                    </button>
                  )}

                  {/* Clear button */}
                  <button
                    onClick={() => cardCount > 0 && !isSyncing && handleClear(tcgType)}
                    disabled={isSyncing}
                    className={`p-2 rounded-lg transition-colors ${
                      isSyncing ? 'text-white/20 cursor-not-allowed' :
                      cardCount > 0 ? 'text-[#EF4444] hover:bg-red-500/10 cursor-pointer' :
                                      'text-white/20 cursor-not-allowed'
                    }`}
                    title={cardCount > 0 ? l('Limpiar datos', 'Clear data') : l('Sin datos', 'No data')}
                    data-testid={`clear-btn-${tcgType}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
